/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/Icon';
import { useUserId } from '@/api/hooks';
import {
  getPlants,
  getUserSettings,
  saveUserSettings,
  type HomePreference,
  type ThemeSetting,
} from '@/api/settings';
import { useUiStore } from '@/stores/uiStore';
import { Menu } from './Menu';

/**
 * Header plant switcher. Lists the plants from the backend catalog, applies the choice to the
 * global {@link useUiStore} plant (used by every manufacturing workflow) and persists it to the
 * user's profile so it is restored on the next visit — mirroring the language switcher.
 */
export function PlantMenu({
  open,
  onOpenChange,
  returnFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where Escape sends focus while the trigger is folded into "More". */
  returnFocus?: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const gmin = useUserId();
  const queryClient = useQueryClient();
  const theme = useUiStore((s) => s.theme);
  const plant = useUiStore((s) => s.plant);
  const setPlant = useUiStore((s) => s.setPlant);

  // Plants catalog is not user-scoped — always fetch so the switcher works even while account/gmin
  // is still resolving (or after a brief backend outage). Persist still requires a GMIN.
  const plantsQuery = useQuery({
    queryKey: ['plants'],
    queryFn: getPlants,
    retry: 1,
    staleTime: 60_000,
    refetchOnMount: 'always',
  });
  const settingsQuery = useQuery({
    queryKey: ['user-settings', gmin],
    queryFn: () => getUserSettings(gmin),
    enabled: Boolean(gmin),
    retry: 1,
  });

  const plants = plantsQuery.data ?? [];
  const currentLabel = plant.plantName || plant.plantCode || t('topbar.plant.none');

  // Persist alongside the user's existing language/home/theme so nothing else is disturbed.
  const persistPlant = (plantCode: string) => {
    if (!gmin) return;
    const s = settingsQuery.data;
    void saveUserSettings(gmin, {
      plant: plantCode,
      language: s?.languageDisplay?.languageCode ?? 'en',
      preference: (s?.preferenceCode === 'manufacturer'
        ? 'manufacturer'
        : 'dealer') as HomePreference,
      theme: theme as ThemeSetting,
    })
      .then(() => queryClient.invalidateQueries({ queryKey: ['user-settings', gmin] }))
      .catch(() => {
        /* persisting the plant is best-effort; the in-session selection already applied */
      });
  };

  const select = (p: { plantCode: string; plantName: string; eoltCountryPlantLine?: string }) => {
    onOpenChange(false);
    if (p.plantCode === plant.plantCode && p.eoltCountryPlantLine === plant.eoltCountryPlantLine) {
      return;
    }
    setPlant({
      plantCode: p.plantCode,
      plantName: p.plantName,
      eoltCountryPlantLine: p.eoltCountryPlantLine,
    });
    persistPlant(p.plantCode);
  };

  return (
    <Menu
      open={open}
      onOpenChange={onOpenChange}
      returnFocus={returnFocus}
      className="tb-plant-menu"
      trigger={({ open: isOpen, toggle }) => (
        <button
          className="tb-plant"
          title={`${t('topbar.plant.title')}: ${currentLabel}`}
          data-tour="plant"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label={`${t('topbar.plant.title')}: ${currentLabel}`}
          onClick={toggle}
        >
          <Icon name="factory" size={16} />
          <span className="tb-plant-name">{currentLabel}</span>
          {/* Below 1280px with a vehicle in the header the pill shrinks to the plant's code. */}
          <span className="tb-plant-code" aria-hidden="true">
            {plant.plantCode || t('topbar.plant.title')}
          </span>
        </button>
      )}
    >
      <div className="lang-head">
        <span className="lang-head-icon">
          <Icon name="factory" size={20} />
        </span>
        <div className="lang-head-text">
          <div className="lang-head-title">{t('topbar.plant.title')}</div>
          <div className="lang-head-current">{currentLabel}</div>
        </div>
      </div>
      <div className="lang-list" role="group" aria-label={t('topbar.plant.title')}>
        {plants.length === 0 ? (
          <p className="tb-plant-empty">{t('topbar.plant.empty')}</p>
        ) : (
          plants.map((p) => {
            const selected = p.plantCode === plant.plantCode;
            return (
              <button
                key={p.plantId}
                type="button"
                aria-current={selected ? 'true' : undefined}
                className={`lang-item${selected ? ' on' : ''}`}
                onClick={() => select(p)}
              >
                <span className="lang-item-flag">{p.plantCode}</span>
                <span className="lang-item-text">
                  <span className="lang-item-name">{p.plantName}</span>
                  {p.plantAbbr && <span className="lang-item-sub">{p.plantAbbr}</span>}
                </span>
                <span className="lang-item-check" aria-hidden="true">
                  {selected && <Icon name="check" size={16} />}
                </span>
              </button>
            );
          })
        )}
      </div>
    </Menu>
  );
}
